import '../belege/belege.css';
import '../briefe/briefe.css';
import './posteingang.css';
import { useEffect, useRef, useState } from 'react';
import { api, errorText } from '../../api';
import { canShowPdf, PdfPages } from '../../components/Pdf';
import { fmtDate, fmtDayMonth, plural } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { KiHinweis } from '../KiHinweis';
import type { PostItem, PostResponse } from './types';

// Posteingang (nur Admin, Sam 13.10.): Die eingescannte Post der Kanzlei hochladen, eine Datei je Brief oder der ganze
// Stapel in einer PDF (die KI teilt ihn in Briefe). Zu jedem Brief schlägt die App den Mandanten vor (gelb, aus
// Steuernummer, Name und Anschrift). Zuordnen ist immer ein Klick: Dann hat der Mandant den Brief in der App und
// bekommt eine Meldung, die Kanzlei bearbeitet ihn wie jeden Brief bis „Erledigt“.

const IMAGE = /^image\/(jpeg|png|gif|webp)$/;
// Hat ein Mandant die KI aus, teilt sie keinen Stapel und liest nur, was die App sicher zuordnet (posteingang/ki.ts)
const NICHT_ALLE =
  'Nicht alle Mandanten haben der KI zugestimmt: Sie bekommt nur Briefe, die die App am Text sicher einem Mandanten ' +
  'mit KI zuordnet. Einen Stapel teilen Sie selbst unter „Ansehen“.';
const running = (i: PostItem) => i.status === 'teilen' || i.status === 'laeuft';
// Seiten: „S. 4–6 aus Scan.pdf“ bei Teilen eines Stapels, sonst „3 Seiten“
function pages(i: PostItem) {
  if (!i.seiten || i.seiten < 1) return null;
  return plural(i.seiten, 'Seite', 'Seiten');
}

export function Posteingang() {
  const toast = useToast();
  const res = useApi<PostResponse>('/api/kanzlei/posteingang', { live: ['posteingang', 'clients'] });
  const [pick, setPick] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<string | null>(null);
  const [up, setUp] = useState<{ done: number; total: number; errors: { name: string; error: string }[] } | null>(null);
  const [over, setOver] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = 'Posteingang · Elbcons Kanzlei';
  }, []);

  const d = res.data;
  const items = d?.items ?? [];
  const chosen = (i: PostItem) => pick[i.id] ?? i.vorschlag?.clientId ?? '';
  const sure = items.filter((i) => !running(i) && i.vorschlag?.sicher && chosen(i) === i.vorschlag.clientId);
  const reading = items.filter(running).length;

  const send = async (files: FileList | null) => {
    const list = [...(files ?? [])];
    if (!list.length || up) return;
    const errors: { name: string; error: string }[] = [];
    setUp({ done: 0, total: list.length, errors });
    for (let n = 0; n < list.length; n++) {
      const f = list[n]!;
      const fd = new FormData();
      fd.append('datei', f);
      try {
        await api.post('/api/kanzlei/posteingang', fd);
      } catch (err) {
        errors.push({ name: f.name, error: errorText(err) });
      }
      setUp({ done: n + 1, total: list.length, errors: [...errors] });
      // die Liste füllt sich schon während des Hochladens
      if (n % 5 === 4) void res.reload();
    }
    await res.reload();
    const ok = list.length - errors.length;
    toast(ok ? `${plural(ok, 'Datei', 'Dateien')} im Posteingang · die KI liest sie jetzt` : 'Nichts hochgeladen');
    setUp(errors.length ? { done: list.length, total: list.length, errors } : null);
  };

  const act = async (run: () => Promise<unknown>, msg: string | null) => {
    setBusy(true);
    try {
      await run();
      if (msg) toast(msg);
      await res.reload();
      return true;
    } catch (err) {
      toast(errorText(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const clientName = (id: string) => d?.clients.find((c) => c.id === id)?.name ?? 'Mandant';
  const assign = (i: PostItem) => {
    const clientId = chosen(i);
    return act(
      () => api.post(`/api/kanzlei/posteingang/${i.id}/zuordnen`, { clientId }),
      `Zugeordnet: ${clientName(clientId)} · der Mandant sieht den Brief jetzt in der App`,
    );
  };

  const assignAll = async () => {
    setBusy(true);
    try {
      const r = await api.post<{ zugeordnet: number; fehler: { id: string; error: string }[] }>(
        '/api/kanzlei/posteingang/zuordnen',
        { items: sure.map((i) => ({ id: i.id, clientId: chosen(i) })) },
      );
      toast(
        r.fehler.length
          ? `${plural(r.zugeordnet, 'Brief', 'Briefe')} zugeordnet, ${r.fehler.length} nicht: ${r.fehler[0]!.error}`
          : `${plural(r.zugeordnet, 'Brief', 'Briefe')} zugeordnet · die Mandanten sehen sie jetzt in der App`,
      );
      setConfirmAll(false);
      await res.reload();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const current = open ? items.find((i) => i.id === open) : undefined;
  // nach dem Zuordnen im Fenster gleich der nächste Brief
  const nextAfter = (id: string) => {
    const at = items.findIndex((i) => i.id === id);
    return items.slice(at + 1).find((i) => !running(i))?.id ?? items.slice(0, at).find((i) => !running(i))?.id ?? null;
  };

  return (
    <div className="pst">
      <div className="head">
        <div>
          <h1>Posteingang</h1>
          <p>Eingescannte Post hochladen, die App schlägt zu jedem Brief den Mandanten vor. Sie ordnen zu.</p>
        </div>
      </div>
      {!d ? (
        res.error ? (
          <p className="note">{res.error}</p>
        ) : (
          <div className="spinner" aria-label="Lädt" />
        )
      ) : (
        <>
          <div className="card">
            {up && up.done < up.total ? (
              <div className="pst-up" role="status">
                <b>
                  Lädt hoch: {up.done} von {up.total}
                </b>
                <progress max={up.total} value={up.done} />
              </div>
            ) : (
              <button
                type="button"
                className={`drop${over ? ' over' : ''}`}
                onClick={() => input.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOver(true);
                }}
                onDragLeave={() => setOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setOver(false);
                  void send(e.dataTransfer.files);
                }}
              >
                <b>Eingescannte Briefe hierher ziehen oder klicken</b>
                <small>
                  Eine Datei je Brief oder der ganze Stapel in einer PDF
                  {d.kiEnabled && d.kiAlle ? ' (die KI teilt ihn in einzelne Briefe)' : ''}
                  {' · '}PDF oder Foto · bis 80 MB je Datei
                </small>
              </button>
            )}
            <input
              ref={input}
              type="file"
              multiple
              accept="application/pdf,image/*"
              hidden
              data-testid="post-pick"
              onChange={(e) => {
                void send(e.target.files);
                e.target.value = '';
              }}
            />
            {up && up.errors.length > 0 && (
              <div className="pst-err" role="alert">
                <b>{plural(up.errors.length, 'Datei', 'Dateien')} nicht hochgeladen:</b>
                <ul>
                  {up.errors.map((e, n) => (
                    <li key={n}>
                      {e.name}: {e.error}
                    </li>
                  ))}
                </ul>
                <button type="button" className="btn sm" onClick={() => setUp(null)}>
                  Ok
                </button>
              </div>
            )}
            {d.kiEnabled ? (
              <KiHinweis test={d.kiTest}>
                Die KI (über Google Cloud, Rechenzentren in der EU) liest {d.kiAlle ? 'jeden Brief' : 'die Briefe'}: Absender,
                worum es geht und an wen er geht. Den Mandanten sucht die App selbst, die Liste der Mandanten geht nicht an
                die KI.
                {d.kiAlle ? '' : ` ${NICHT_ALLE}`}
              </KiHinweis>
            ) : (
              <p className="note">
                Die KI ist aus: Den Mandanten schlägt die App nur vor, wenn die PDF Text hat (Scanner mit Texterkennung). Einen
                Stapel teilen Sie selbst unter „Ansehen“.
              </p>
            )}
          </div>

          <div className="card">
            <div className="ch">
              <h3>{items.length ? `Im Posteingang (${items.length})` : 'Im Posteingang'}</h3>
              {sure.length > 0 && !confirmAll && (
                <button type="button" className="btn sm dark" disabled={busy} onClick={() => setConfirmAll(true)}>
                  Alle sicheren zuordnen ({sure.length})
                </button>
              )}
            </div>
            {confirmAll && sure.length > 0 && (
              <div className="pst-ok" role="alert">
                <span>
                  {sure.length === 1 ? 'Einen Brief' : `${sure.length} Briefe`} den vorgeschlagenen Mandanten zuordnen? Jeder
                  Mandant sieht seinen Brief dann in der App und bekommt eine Meldung.
                </span>
                <span className="row">
                  <button type="button" className="btn sm dark" disabled={busy} onClick={() => void assignAll()}>
                    Ja, zuordnen
                  </button>
                  <button type="button" className="btn sm" onClick={() => setConfirmAll(false)}>
                    Abbrechen
                  </button>
                </span>
              </div>
            )}
            {reading > 0 && (
              <p className="note pst-reading" role="status">
                <span className="dot" aria-hidden="true" />
                {d.kiEnabled ? 'Die KI liest' : 'Die App liest'} noch {plural(reading, 'Datei', 'Dateien')} …
              </p>
            )}
            {!items.length && <p className="note">Der Posteingang ist leer. Eingescannte Briefe oben hochladen.</p>}
            {items.map((i) => (
              <PostRow
                key={i.id}
                item={i}
                clients={d.clients}
                value={chosen(i)}
                busy={busy}
                onPick={(v) => setPick((p) => ({ ...p, [i.id]: v }))}
                onAssign={() => void assign(i)}
                onOpen={() => setOpen(i.id)}
              />
            ))}
          </div>
        </>
      )}
      {open && d && (
        <PostDialog
          key={open}
          item={current}
          kiEnabled={d.kiEnabled}
          kiAlle={d.kiAlle}
          clients={d.clients}
          value={current ? chosen(current) : ''}
          busy={busy}
          onPick={(v) => setPick((p) => ({ ...p, [open]: v }))}
          onAssign={async () => {
            if (!current) return;
            const next = nextAfter(current.id);
            if (await assign(current)) setOpen(next);
          }}
          act={act}
          onGo={setOpen}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

function ClientSelect({
  clients,
  value,
  suggested,
  disabled,
  onPick,
}: {
  clients: PostResponse['clients'];
  value: string;
  suggested: string | null;
  disabled: boolean;
  onPick(v: string): void;
}) {
  return (
    <select
      className={value && value === suggested ? 'ki-pick' : undefined}
      value={value}
      disabled={disabled}
      onChange={(e) => onPick(e.target.value)}
      aria-label="Mandant"
    >
      <option value="">Mandant wählen …</option>
      {clients.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      ))}
    </select>
  );
}

function Status({ item }: { item: PostItem }) {
  if (item.status === 'teilen') return <span className="pill p-info">KI teilt den Stapel in Briefe …</span>;
  if (item.status === 'laeuft') return <span className="pill p-info">wird gelesen …</span>;
  if (item.status === 'fehler') return <span className="pill p-bad">{item.fehler}</span>;
  return null;
}

function Vorschlag({ item }: { item: PostItem }) {
  if (running(item)) return null;
  const v = item.vorschlag;
  if (!v) return <small className="pv none">Kein Mandant erkannt, bitte selbst wählen.</small>;
  return (
    <small className={`pv${v.sicher ? ' sure' : ''}`}>
      ✨ Vorschlag: <b>{v.clientName}</b>
      {v.grund ? ` · ${v.grund}` : ''} · {v.sicher ? 'passt eindeutig' : 'bitte prüfen'}
    </small>
  );
}

function PostRow({
  item,
  clients,
  value,
  busy,
  onPick,
  onAssign,
  onOpen,
}: {
  item: PostItem;
  clients: PostResponse['clients'];
  value: string;
  busy: boolean;
  onPick(v: string): void;
  onAssign(): void;
  onOpen(): void;
}) {
  const p = pages(item);
  return (
    <div className="task pst-r" data-testid="post-row">
      <span className="pst-i" aria-hidden="true">
        ✉
      </span>
      <div className="tx">
        <b>{item.ki ? item.ki.absender : running(item) ? 'Neuer Scan' : 'Brief'}</b> <Status item={item} />
        {item.ki && <small className="ki-b">{item.ki.betreff}</small>}
        {item.ohneKi && <small className="note">{item.ohneKi}</small>}
        <small>
          📄 {item.name}
          {p ? ` · ${p}` : ''} · eingescannt am {fmtDayMonth(item.createdAt)}
          {item.createdBy ? ` von ${item.createdBy}` : ''}
        </small>
        <Vorschlag item={item} />
      </div>
      <div className="pst-a">
        <ClientSelect
          clients={clients}
          value={value}
          suggested={item.vorschlag?.clientId ?? null}
          disabled={busy || item.status === 'teilen'}
          onPick={onPick}
        />
        <button type="button" className="btn sm dark" disabled={busy || !value || item.status === 'teilen'} onClick={onAssign}>
          Zuordnen
        </button>
        <button type="button" className="btn sm" onClick={onOpen}>
          Ansehen
        </button>
      </div>
    </div>
  );
}

// Brief ansehen: Vorschau, was die KI gelesen hat, zuordnen, von Hand teilen oder zusammenfügen, entfernen
function PostDialog({
  item,
  kiEnabled,
  kiAlle,
  clients,
  value,
  busy,
  onPick,
  onAssign,
  act,
  onGo,
  onClose,
}: {
  item: PostItem | undefined;
  kiEnabled: boolean;
  kiAlle: boolean;
  clients: PostResponse['clients'];
  value: string;
  busy: boolean;
  onPick(v: string): void;
  onAssign(): void;
  act(run: () => Promise<unknown>, msg: string | null): Promise<boolean>;
  onGo(id: string | null): void;
  onClose(): void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [ab, setAb] = useState(2);
  const [ask, setAsk] = useState(false);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);

  const i = item;
  const fileUrl = i ? `/api/kanzlei/posteingang/${i.id}/file` : '';
  const multi = !!i && i.mime === 'application/pdf' && (i.seiten ?? 1) > 1;
  const idle = !!i && !running(i);

  const split = () =>
    act(async () => {
      const r = await api.post<{ ids: string[] }>(`/api/kanzlei/posteingang/${i!.id}/teilen`, { abSeite: ab });
      onGo(r.ids[0] ?? null);
    }, `Geteilt: ab Seite ${ab} ein neuer Brief`);
  const merge = () =>
    act(async () => {
      const r = await api.post<{ ids: string[] }>(`/api/kanzlei/posteingang/${i!.id}/zusammen`);
      onGo(r.ids[0] ?? null);
    }, 'Zusammengefügt');
  const retry = () => act(() => api.post(`/api/kanzlei/posteingang/${i!.id}/ki`), null);
  const remove = () =>
    act(async () => {
      await api.del(`/api/kanzlei/posteingang/${i!.id}`);
      onClose();
    }, 'Entfernt');

  return (
    <dialog
      ref={ref}
      id="lview"
      className="blg ltr pst-d"
      aria-label={i ? `Posteingang: ${i.name}` : 'Posteingang'}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="lv-h">
        <b>{i?.ki ? `${i.ki.absender} · Posteingang` : 'Posteingang'}</b>
        <span className="note">
          {i
            ? `${i.name}${pages(i) ? ` · ${pages(i)}` : ''} · eingescannt am ${fmtDate(i.createdAt)}${i.createdBy ? ` von ${i.createdBy}` : ''}`
            : 'Dieser Brief ist nicht mehr im Posteingang.'}
        </span>
        {i && (
          <a className="btn sm" href={`${fileUrl}?download=1`} download>
            Herunterladen
          </a>
        )}
        <button type="button" className="btn sm dark" onClick={onClose} autoFocus>
          Schließen
        </button>
      </div>
      {i && (
        <>
          {IMAGE.test(i.mime) ? (
            <div className="lv-b">
              <img src={fileUrl} alt={i.name} />
            </div>
          ) : i.mime === 'application/pdf' && canShowPdf() ? (
            <object key={fileUrl} data={fileUrl} type="application/pdf" className="lv-b" aria-label={i.name}>
              <p className="note lv-np">Die PDF kann hier nicht angezeigt werden. Bitte herunterladen.</p>
            </object>
          ) : i.mime === 'application/pdf' ? (
            <div className="lv-b">
              <PdfPages
                key={fileUrl}
                url={fileUrl}
                title={i.name}
                fallback={<p className="note lv-np">Die PDF kann hier nicht angezeigt werden. Bitte herunterladen.</p>}
              />
            </div>
          ) : (
            <div className="lv-b">
              <p className="note lv-np">Vorschau nicht verfügbar. Bitte herunterladen.</p>
            </div>
          )}
          <div className="lv-c">
            <div className="lbar">
              <label>
                Mandant
                <ClientSelect
                  clients={clients}
                  value={value}
                  suggested={i.vorschlag?.clientId ?? null}
                  disabled={busy || i.status === 'teilen'}
                  onPick={onPick}
                />
              </label>
              <button type="button" className="btn sm dark" disabled={busy || !value || i.status === 'teilen'} onClick={onAssign}>
                Zuordnen
              </button>
              <Status item={i} />
              <span className="grow" />
              {/* ohne KI gelesen, weil nicht alle zugestimmt haben: noch einmal hilft erst, wenn alle zugestimmt haben */}
              {kiEnabled && idle && (i.status === 'fehler' || (!i.ki && (!i.ohneKi || kiAlle))) && (
                <button type="button" className="btn sm" disabled={busy} onClick={() => void retry()}>
                  {i.status === 'fehler' ? 'Noch einmal versuchen' : 'Mit der KI lesen'}
                </button>
              )}
            </div>
            <div className="pst-v">
              <Vorschlag item={i} />
              {i.ohneKi && <small className="note">{i.ohneKi}</small>}
            </div>
            {i.ki && (
              <div className="lki">
                <div className="lki-h">
                  <span className="lki-t">✨ {i.ki.betreff}</span>
                  {i.ki.frist && <span className="note">Frist laut KI: {fmtDate(i.ki.frist)}</span>}
                </div>
                <p>{i.ki.zusammenfassung}</p>
                {i.ki.hinweis && <p className="note">{i.ki.hinweis}</p>}
                <p className="note">
                  Vorschlag der KI, bitte prüfen. Zusammenfassung und Antwortvorschlag stehen nach dem Zuordnen beim Brief (nur
                  für die Kanzlei).
                </p>
              </div>
            )}
            {idle && (multi || i.nextId) && (
              <div className="lbar pst-t">
                {multi && (
                  <label>
                    Hier stecken mehrere Briefe? Neuer Brief ab Seite
                    <select value={ab} onChange={(e) => setAb(Number(e.target.value))} aria-label="Neuer Brief ab Seite">
                      {Array.from({ length: (i.seiten ?? 1) - 1 }, (_, n) => n + 2).map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                    <button type="button" className="btn sm" disabled={busy} onClick={() => void split()}>
                      Teilen
                    </button>
                  </label>
                )}
                {i.nextId && (
                  <button type="button" className="btn sm" disabled={busy} onClick={() => void merge()}>
                    Mit dem nächsten Teil zusammenfügen
                  </button>
                )}
              </div>
            )}
            <div className="lbar">
              {ask ? (
                <>
                  <span>Wirklich entfernen? Die Datei wird gelöscht (z. B. Werbung oder doppelt eingescannt).</span>
                  <button type="button" className="btn sm dark" disabled={busy} onClick={() => void remove()}>
                    Ja, entfernen
                  </button>
                  <button type="button" className="btn sm" onClick={() => setAsk(false)}>
                    Abbrechen
                  </button>
                </>
              ) : (
                <button type="button" className="btn sm pst-del" onClick={() => setAsk(true)}>
                  Entfernen
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </dialog>
  );
}
